import React, { useState, useEffect } from 'react';
import { Table, Button, Badge, Spinner, Alert, Modal, Form } from 'react-bootstrap';
import { adminApi } from '../../services/adminApi';
import UsageCell from './UsageCell';
import { PLAN_LIMITS, PlanName, LimitOverrides } from '../../types/usage.types';

interface UsageData {
  messageCount: number;
  artifactCount: number;
  figmaExportCount: number;
  pngExportCount?: number;
  jpegExportCount?: number;
  svgExportCount?: number;
  lifetimeMessageCount: number;
  lifetimeArtifactCount: number;
  lifetimeFigmaExportCount: number;
  lifetimePngExportCount?: number;
  lifetimeJpegExportCount?: number;
  lifetimeSvgExportCount?: number;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
}

interface User {
  id: string;
  email: string;
  username?: string;
  createdAt?: string; // ISO date string from backend
  disabled?: boolean;
  role?: string;
  totalTokens?: number;
  inputTokens?: number;
  outputTokens?: number;
  projectCount?: number;
  usage?: UsageData;
  subscription?: {
    status?: string;
    planName?: string;
    planId?: string;
    currentPeriodStart?: string;
    currentPeriodEnd?: string;
    cancelAtPeriodEnd?: boolean;
  };
  limitOverrides?: LimitOverrides;
  accountStatus?: 'active' | 'grace_period' | 'pending_deletion';
  gracePeriodEndDate?: string;
}

interface LimitOverrideField {
  value: string;        // Input field value (empty string = no override)
  isUnlimited: boolean; // Checkbox state for unlimited
}

interface EditFormState {
  username: string;
  role: string;
  gracePeriod: boolean;
  limitOverrides: {
    projects: LimitOverrideField;
    messages: LimitOverrideField;
    artifacts: LimitOverrideField;
    figmaExports: LimitOverrideField;
  };
}

const getInitialEditFormState = (): EditFormState => ({
  username: '',
  role: '',
  gracePeriod: false,
  limitOverrides: {
    projects: { value: '', isUnlimited: false },
    messages: { value: '', isUnlimited: false },
    artifacts: { value: '', isUnlimited: false },
    figmaExports: { value: '', isUnlimited: false },
  },
});

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editForm, setEditForm] = useState<EditFormState>(getInitialEditFormState());

  // Claude Sonnet 4.5 pricing
  const INPUT_COST_PER_MILLION = 3.00; // $3 per 1M input tokens
  const OUTPUT_COST_PER_MILLION = 15.00; // $15 per 1M output tokens

  const calculateCost = (tokens: number, costPerMillion: number): number => {
    return (tokens / 1_000_000) * costPerMillion;
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await adminApi.getUsers();
      setUsers(response.data);
    } catch (err: any) {
      setError('Failed to fetch users');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user: User) => {
    setSelectedUser(user);

    // Convert limitOverrides from Firestore format to form state
    const overrides = user.limitOverrides || {};

    const toLimitField = (value: number | null | undefined): LimitOverrideField => {
      if (value === null) {
        return { value: '', isUnlimited: true };
      }
      if (value === undefined) {
        return { value: '', isUnlimited: false };
      }
      return { value: value.toString(), isUnlimited: false };
    };

    setEditForm({
      username: user.username || '',
      role: user.role || '',
      gracePeriod: user.accountStatus === 'grace_period',
      limitOverrides: {
        projects: toLimitField(overrides.projects),
        messages: toLimitField(overrides.messages),
        artifacts: toLimitField(overrides.artifacts),
        figmaExports: toLimitField(overrides.figmaExports),
      },
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedUser) return;

    try {
      // Convert form state to Firestore format for limitOverrides
      const fromLimitField = (field: LimitOverrideField): number | null | undefined => {
        if (field.isUnlimited) {
          return null; // null means unlimited
        }
        if (field.value === '') {
          return undefined; // undefined means no override (will be removed)
        }
        const num = parseInt(field.value, 10);
        return isNaN(num) ? undefined : num;
      };

      const limitOverrides: LimitOverrides = {};
      const projects = fromLimitField(editForm.limitOverrides.projects);
      const messages = fromLimitField(editForm.limitOverrides.messages);
      const artifacts = fromLimitField(editForm.limitOverrides.artifacts);
      const figmaExports = fromLimitField(editForm.limitOverrides.figmaExports);

      // Only include fields that have values (not undefined)
      if (projects !== undefined) limitOverrides.projects = projects;
      if (messages !== undefined) limitOverrides.messages = messages;
      if (artifacts !== undefined) limitOverrides.artifacts = artifacts;
      if (figmaExports !== undefined) limitOverrides.figmaExports = figmaExports;

      // If no overrides, set to null to clear existing overrides
      const hasOverrides = Object.keys(limitOverrides).length > 0;

      // Check if grace period status changed
      const currentGracePeriod = selectedUser.accountStatus === 'grace_period';
      if (editForm.gracePeriod !== currentGracePeriod) {
        await adminApi.setGracePeriod(selectedUser.id, editForm.gracePeriod);
      }

      await adminApi.updateUser(selectedUser.id, {
        username: editForm.username,
        role: editForm.role,
        limitOverrides: hasOverrides ? limitOverrides : null,
      });
      await fetchUsers();
      setShowEditModal(false);
      setSelectedUser(null);
    } catch (err: any) {
      alert('Failed to update user');
    }
  };

  const handleToggleDisable = async (user: User) => {
    try {
      if (user.disabled) {
        await adminApi.enableUser(user.id);
      } else {
        await adminApi.disableUser(user.id);
      }
      await fetchUsers();
    } catch (err: any) {
      alert('Failed to toggle user status');
    }
  };

  const handleDelete = (user: User) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedUser) return;

    try {
      await adminApi.deleteUser(selectedUser.id);
      await fetchUsers();
      setShowDeleteModal(false);
      setSelectedUser(null);
    } catch (err: any) {
      alert('Failed to delete user');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  return (
    <div>
      <Table responsive striped bordered hover>
        <thead>
          <tr>
            <th>Email / ID</th>
            <th>Role</th>
            <th>Status</th>
            <th>Subscription</th>
            <th>Projects</th>
            <th>Messages</th>
            <th>Artifacts</th>
            <th>Figma</th>
            <th>PNG</th>
            <th>JPEG</th>
            <th>SVG</th>
            <th>Token Usage</th>
            <th>Estimated Cost</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => {
            const inputCost = calculateCost(user.inputTokens || 0, INPUT_COST_PER_MILLION);
            const outputCost = calculateCost(user.outputTokens || 0, OUTPUT_COST_PER_MILLION);
            const totalCost = inputCost + outputCost;

            // Get plan limits for this user
            const planName = (user.subscription?.planName || 'starter') as PlanName;
            const baseLimits = PLAN_LIMITS[planName];
            const isStarter = planName === 'starter';
            const overrides = user.limitOverrides;

            // Calculate effective limits (considering overrides)
            const effectiveLimits = {
              projects: overrides?.projects !== undefined ? overrides.projects : baseLimits.projects,
              messages: overrides?.messages !== undefined ? overrides.messages : baseLimits.messages,
              artifacts: overrides?.artifacts !== undefined ? overrides.artifacts : baseLimits.artifacts,
              figmaExports: overrides?.figmaExports !== undefined ? overrides.figmaExports : baseLimits.figmaExports,
            };

            // Check which limits are overridden
            const isProjectsOverridden = overrides?.projects !== undefined;
            const isMessagesOverridden = overrides?.messages !== undefined;
            const isArtifactsOverridden = overrides?.artifacts !== undefined;
            const isFigmaOverridden = overrides?.figmaExports !== undefined;

            // For Starter plan, use lifetime counts; for Pro/Ultra, use period counts
            const messageCount = isStarter
              ? (user.usage?.lifetimeMessageCount || 0)
              : (user.usage?.messageCount || 0);
            const artifactCount = isStarter
              ? (user.usage?.lifetimeArtifactCount || 0)
              : (user.usage?.artifactCount || 0);
            const figmaCount = isStarter
              ? (user.usage?.lifetimeFigmaExportCount || 0)
              : (user.usage?.figmaExportCount || 0);

            // Download counters (analytics only — no plan limit, so we show period
            // count for period-based plans and lifetime count for Starter, matching
            // how the other usage cells are displayed.)
            const pngCount = isStarter
              ? (user.usage?.lifetimePngExportCount || 0)
              : (user.usage?.pngExportCount || 0);
            const jpegCount = isStarter
              ? (user.usage?.lifetimeJpegExportCount || 0)
              : (user.usage?.jpegExportCount || 0);
            const svgCount = isStarter
              ? (user.usage?.lifetimeSvgExportCount || 0)
              : (user.usage?.svgExportCount || 0);

            return (
              <tr key={user.id}>
                <td>
                  <span className="admin-table-cell-email">{user.email}</span>
                  <br />
                  <span className='admin-user-id'>{user.id}</span>
                </td>
                <td className="admin-table-cell-small">{user.role || 'user'}</td>
                <td>
                  <Badge bg={user.disabled ? 'danger' : 'success'}>
                    {user.disabled ? 'Disabled' : 'Active'}
                  </Badge>
                </td>
                <td className="admin-table-cell-small">
                  <div>
                    <div>
                      <Badge
                        bg={
                          user.subscription?.planName === 'ultra' ? 'primary' :
                          user.subscription?.planName === 'pro' ? 'info' :
                          'secondary'
                        }
                      >
                        {user.subscription?.planName?.toUpperCase() || 'STARTER'}
                      </Badge>
                    </div>
                    {user.subscription?.status && (
                      <div className="admin-subscription-status">
                        {user.subscription.status}
                        {user.subscription.cancelAtPeriodEnd && ' (canceling)'}
                      </div>
                    )}
                  </div>
                </td>
                <td>
                  <UsageCell
                    current={user.projectCount || 0}
                    limit={effectiveLimits.projects}
                    isLifetime={true}
                    isOverridden={isProjectsOverridden}
                  />
                </td>
                <td>
                  <UsageCell
                    current={messageCount}
                    limit={effectiveLimits.messages}
                    periodStart={user.subscription?.currentPeriodStart}
                    periodEnd={user.subscription?.currentPeriodEnd}
                    isLifetime={isStarter}
                    isOverridden={isMessagesOverridden}
                  />
                </td>
                <td>
                  <UsageCell
                    current={artifactCount}
                    limit={effectiveLimits.artifacts}
                    periodStart={user.subscription?.currentPeriodStart}
                    periodEnd={user.subscription?.currentPeriodEnd}
                    isLifetime={isStarter}
                    isOverridden={isArtifactsOverridden}
                  />
                </td>
                <td>
                  <UsageCell
                    current={figmaCount}
                    limit={effectiveLimits.figmaExports}
                    periodStart={user.subscription?.currentPeriodStart}
                    periodEnd={user.subscription?.currentPeriodEnd}
                    isLifetime={isStarter}
                    isOverridden={isFigmaOverridden}
                  />
                </td>
                <td className="admin-table-cell-small">{pngCount}</td>
                <td className="admin-table-cell-small">{jpegCount}</td>
                <td className="admin-table-cell-small">{svgCount}</td>
                <td>
                  <div className="admin-token-usage">
                    <div><strong>{(user.totalTokens || 0).toLocaleString()}</strong> total</div>
                    <div className="admin-token-usage-detail">
                      {(user.inputTokens || 0).toLocaleString()} in / {(user.outputTokens || 0).toLocaleString()} out
                    </div>
                  </div>
                </td>
                <td>
                  <div className="admin-token-usage">
                    <div><strong>${totalCost.toFixed(4)}</strong> total</div>
                    <div className="admin-token-usage-detail">
                      ${inputCost.toFixed(4)} in / ${outputCost.toFixed(4)} out
                    </div>
                  </div>
                </td>
                <td className="admin-table-cell-small">
                  {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                </td>
                <td>
                  <div className="d-flex gap-2">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleEdit(user)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant={user.disabled ? 'outline-success' : 'outline-warning'}
                      size="sm"
                      onClick={() => handleToggleDisable(user)}
                    >
                      {user.disabled ? 'Enable' : 'Disable'}
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDelete(user)}
                    >
                      Delete
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" dialogClassName="admin-edit-modal">
        <Modal.Header closeButton>
          <Modal.Title>Edit User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Username</Form.Label>
              <Form.Control
                type="text"
                value={editForm.username}
                onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Role</Form.Label>
              <Form.Select
                value={editForm.role}
                onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
                <option value="moderator">Moderator</option>
              </Form.Select>
            </Form.Group>

            <hr />
            <h6 className="mb-3">Account Status</h6>
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="gracePeriodSwitch"
                label="Grace Period Mode"
                checked={editForm.gracePeriod}
                onChange={(e) => setEditForm({ ...editForm, gracePeriod: e.target.checked })}
              />
              <Form.Text className="text-muted">
                When enabled, user will be in a 30-day grace period. They can view data but cannot create new content.
                {selectedUser?.gracePeriodEndDate && selectedUser.accountStatus === 'grace_period' && (
                  <span className="d-block mt-1">
                    Current grace period ends: <strong>{new Date(selectedUser.gracePeriodEndDate).toLocaleDateString()}</strong>
                  </span>
                )}
              </Form.Text>
            </Form.Group>

            <hr />
            <h6 className="mb-3">Limit Overrides</h6>
            <p className="text-muted small mb-3">
              Leave empty to use plan defaults. Check "Unlimited" for no limit.
              {selectedUser && (
                <span className="d-block mt-1">
                  Current plan: <strong>{selectedUser.subscription?.planName?.toUpperCase() || 'STARTER'}</strong>
                </span>
              )}
            </p>

            {/* Projects Override */}
            <Form.Group className="mb-3">
              <Form.Label>
                Projects
                <span className="text-muted ms-2 admin-form-label-subtext">
                  (Plan default: {PLAN_LIMITS[(selectedUser?.subscription?.planName as PlanName) || 'starter']?.projects || 1})
                </span>
              </Form.Label>
              <div className="d-flex align-items-center gap-3">
                <Form.Control
                  type="number"
                  min="0"
                  placeholder="No override"
                  value={editForm.limitOverrides.projects.value}
                  disabled={editForm.limitOverrides.projects.isUnlimited}
                  onChange={(e) => setEditForm({
                    ...editForm,
                    limitOverrides: {
                      ...editForm.limitOverrides,
                      projects: { ...editForm.limitOverrides.projects, value: e.target.value },
                    },
                  })}
                  className="admin-limit-override-input"
                />
                <Form.Check
                  type="checkbox"
                  label="Unlimited"
                  checked={editForm.limitOverrides.projects.isUnlimited}
                  onChange={(e) => setEditForm({
                    ...editForm,
                    limitOverrides: {
                      ...editForm.limitOverrides,
                      projects: { value: '', isUnlimited: e.target.checked },
                    },
                  })}
                />
              </div>
            </Form.Group>

            {/* Messages Override */}
            <Form.Group className="mb-3">
              <Form.Label>
                Messages
                <span className="text-muted ms-2 admin-form-label-subtext">
                  (Plan default: {PLAN_LIMITS[(selectedUser?.subscription?.planName as PlanName) || 'starter']?.messages || 100})
                </span>
              </Form.Label>
              <div className="d-flex align-items-center gap-3">
                <Form.Control
                  type="number"
                  min="0"
                  placeholder="No override"
                  value={editForm.limitOverrides.messages.value}
                  disabled={editForm.limitOverrides.messages.isUnlimited}
                  onChange={(e) => setEditForm({
                    ...editForm,
                    limitOverrides: {
                      ...editForm.limitOverrides,
                      messages: { ...editForm.limitOverrides.messages, value: e.target.value },
                    },
                  })}
                  className="admin-limit-override-input"
                />
                <Form.Check
                  type="checkbox"
                  label="Unlimited"
                  checked={editForm.limitOverrides.messages.isUnlimited}
                  onChange={(e) => setEditForm({
                    ...editForm,
                    limitOverrides: {
                      ...editForm.limitOverrides,
                      messages: { value: '', isUnlimited: e.target.checked },
                    },
                  })}
                />
              </div>
            </Form.Group>

            {/* Artifacts Override */}
            <Form.Group className="mb-3">
              <Form.Label>
                Artifacts
                <span className="text-muted ms-2 admin-form-label-subtext">
                  (Plan default: {PLAN_LIMITS[(selectedUser?.subscription?.planName as PlanName) || 'starter']?.artifacts || 5})
                </span>
              </Form.Label>
              <div className="d-flex align-items-center gap-3">
                <Form.Control
                  type="number"
                  min="0"
                  placeholder="No override"
                  value={editForm.limitOverrides.artifacts.value}
                  disabled={editForm.limitOverrides.artifacts.isUnlimited}
                  onChange={(e) => setEditForm({
                    ...editForm,
                    limitOverrides: {
                      ...editForm.limitOverrides,
                      artifacts: { ...editForm.limitOverrides.artifacts, value: e.target.value },
                    },
                  })}
                  className="admin-limit-override-input"
                />
                <Form.Check
                  type="checkbox"
                  label="Unlimited"
                  checked={editForm.limitOverrides.artifacts.isUnlimited}
                  onChange={(e) => setEditForm({
                    ...editForm,
                    limitOverrides: {
                      ...editForm.limitOverrides,
                      artifacts: { value: '', isUnlimited: e.target.checked },
                    },
                  })}
                />
              </div>
            </Form.Group>

            {/* Figma Exports Override */}
            <Form.Group className="mb-3">
              <Form.Label>
                Figma Exports
                <span className="text-muted ms-2 admin-form-label-subtext">
                  (Plan default: {PLAN_LIMITS[(selectedUser?.subscription?.planName as PlanName) || 'starter']?.figmaExports ?? 'Unlimited'})
                </span>
              </Form.Label>
              <div className="d-flex align-items-center gap-3">
                <Form.Control
                  type="number"
                  min="0"
                  placeholder="No override"
                  value={editForm.limitOverrides.figmaExports.value}
                  disabled={editForm.limitOverrides.figmaExports.isUnlimited}
                  onChange={(e) => setEditForm({
                    ...editForm,
                    limitOverrides: {
                      ...editForm.limitOverrides,
                      figmaExports: { ...editForm.limitOverrides.figmaExports, value: e.target.value },
                    },
                  })}
                  className="admin-limit-override-input"
                />
                <Form.Check
                  type="checkbox"
                  label="Unlimited"
                  checked={editForm.limitOverrides.figmaExports.isUnlimited}
                  onChange={(e) => setEditForm({
                    ...editForm,
                    limitOverrides: {
                      ...editForm.limitOverrides,
                      figmaExports: { value: '', isUnlimited: e.target.checked },
                    },
                  })}
                />
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="admin-modal-footer">
          <Button
            variant="link"
            onClick={() => setShowEditModal(false)}
            className="admin-modal-btn"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveEdit}
            className="admin-modal-btn"
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete user {selectedUser?.email}? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer className="admin-modal-footer">
          <Button
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
            className="admin-delete-btn-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={confirmDelete}
            className="admin-delete-btn-confirm"
          >
            Delete User
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UserManagement;